import React, { useState, useEffect, useMemo } from 'react';
import {
  Search, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw, ShieldAlert,
  GitCompare, Info, ChevronRight, ChevronDown, AlertTriangle
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './NpsLedger.css';

const npsClass = (n) => (n > 0 ? 'pos' : n < 0 ? 'neg' : 'zero');
const fmtNps = (n) => (n === null || n === undefined ? '—' : `${n > 0 ? '+' : ''}${n}`);

// One of the three column tallies rendered as a compact stat block.
const Tally = ({ label, tally, tone, foot }) => (
  <div className={`nl-ocard ${tone || ''}`}>
    <div className="nl-ocard-label">{label}</div>
    <div className={`nl-ocard-nps ${tally ? npsClass(tally.nps) : ''}`}>{tally ? tally.nps : '—'}</div>
    {tally && (
      <div className="nl-ocard-sub">
        <span>{tally.total.toLocaleString()} responses</span>
        <span className="nl-dot pos">{tally.promoters.toLocaleString()} promoters</span>
        <span className="nl-dot neu">{tally.neutrals.toLocaleString()} neutral</span>
        <span className="nl-dot neg">{tally.detractors.toLocaleString()} detractors</span>
      </div>
    )}
    {foot && <div className="nl-ocard-foot">{foot}</div>}
  </div>
);

const NpsLedger = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(''); // '' | 'Sales' | 'Delivery'
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('npsDelta');
  const [sortDir, setSortDir] = useState('desc');
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [expanded, setExpanded] = useState(null); // `${type}\u0001${emp}`
  const { showError } = useToast();

  const fetchData = async (type) => {
    try {
      setLoading(true);
      const res = await api.get('/api/nps/ledger/compare', { params: type ? { type } : {} });
      if (res.data.success) setData(res.data);
      else showError('Failed to load the comparison');
    } catch (e) {
      console.error('compare load failed', e);
      showError('Failed to load the NPS Ledger comparison');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(typeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const casesByEmp = useMemo(() => {
    const m = new Map();
    for (const c of data?.sensitiveCases || []) {
      for (const mem of c.members) {
        const k = `${c.type}\u0001${mem.employee_id}`;
        if (!m.has(k)) m.set(k, []);
        if (!m.get(k).some((x) => x.order_key === c.order_key)) m.get(k).push(c);
      }
    }
    return m;
  }, [data]);

  const rows = useMemo(() => {
    let r = data?.employees || [];
    if (onlyChanged) r = r.filter((e) => e.npsDelta !== 0 || e.replaced > 0);
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((e) => (e.name || '').toLowerCase().includes(q) || (e.employee_id || '').toLowerCase().includes(q));
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (e) => {
      switch (sortField) {
        case 'name': return (e.name || '').toLowerCase();
        case 'today': return e.salesAppToday ? e.salesAppToday.nps : -9999;
        case 'same': return e.newSameRules.nps;
        case 'corrected': return e.newCorrected.nps;
        case 'replaced': return e.replaced;
        case 'responses': return e.newSameRules.total;
        default: return e.npsDelta;
      }
    };
    return [...r].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
  }, [data, onlyChanged, search, sortField, sortDir]);

  const toggleSort = (f) => {
    if (sortField === f) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(f); setSortDir('desc'); }
  };
  const SortIcon = ({ f }) => (sortField !== f
    ? <ArrowUpDown size={13} className="nl-sort" />
    : sortDir === 'asc' ? <ArrowUp size={13} className="nl-sort on" /> : <ArrowDown size={13} className="nl-sort on" />);

  const overall = data?.overall;
  const sum = data?.summary;
  const drift = sum ? sum.parity_mismatches : 0;

  return (
    <div className="nl-wrap">
      <div className="nl-head">
        <div>
          <h1 className="nl-title">NPS Ledger <span className="nl-tag">shadow</span></h1>
          <p className="nl-sub">The new scoring system, running in parallel. Nothing here is live yet — your current NPS page is untouched. This compares today's numbers against the new system so you can see exactly what the corrections would change before switching.</p>
        </div>
        <button className="nl-btn" onClick={() => fetchData(typeFilter)} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      <div className="nl-filters">
        {[['', 'All'], ['Sales', 'Sales'], ['Delivery', 'Delivery']].map(([v, l]) => (
          <button key={v} className={`nl-chip ${typeFilter === v ? 'on' : ''}`} onClick={() => setTypeFilter(v)}>{l}</button>
        ))}
      </div>

      {loading && <div className="nl-loading"><RefreshCw size={22} className="spin" /> Crunching ~31k answers…</div>}

      {!loading && data && (
        <>
          <div className="nl-explain">
            <GitCompare size={18} />
            <div>
              <b>Three views of the same people.</b> <span className="nl-k today">Sales app today</span> is what your live system shows now.
              <span className="nl-k same"> New · same rules</span> is the new system counting <i>every</i> answer exactly like today — it should match.
              <span className="nl-k corr"> New · corrected</span> applies <b>Rule 1 (latest answer wins)</b> in shadow: when an order was answered more than once, only the newest counts.
            </div>
          </div>

          <div className="nl-overall">
            <Tally label="Sales app today" tally={overall.salesAppToday} tone="today" />
            <Tally label="New · same rules" tally={overall.newSameRules} tone="same"
              foot={<span className={`nl-parity ${drift ? 'warn' : 'ok'}`}>{drift ? `${drift} staff drifted since import` : 'matches live exactly'}</span>} />
            <Tally label="New · corrected" tally={overall.newCorrected} tone="corr"
              foot={<span className="nl-effect">{sum.answers_replaced.toLocaleString()} answers replaced · {sum.by_class.different_employee} between different staff</span>} />
          </div>

          {drift > 0 && (
            <div className="nl-drift">
              <Info size={15} />
              Parity was <b>exact</b> at import. Since then the nightly sync added newer answers, so {drift} employees now differ between “today” and the ledger snapshot. Re-running the import re-syncs them — this is drift, not an error.
            </div>
          )}

          <div className="nl-toolbar">
            <div className="nl-search">
              <Search size={16} />
              <input placeholder="Search employee or code…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <label className="nl-toggle">
              <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
              Only employees whose score changes
            </label>
            <span className="nl-count">{rows.length} shown · {sum.employees_changed} change · {sum.by_class.different_employee} sensitive</span>
          </div>

          <div className="nl-tablewrap">
            <table className="nl-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('name')}>Employee <SortIcon f="name" /></th>
                  <th onClick={() => toggleSort('today')}>Today <SortIcon f="today" /></th>
                  <th onClick={() => toggleSort('same')}>New · same <SortIcon f="same" /></th>
                  <th onClick={() => toggleSort('corrected')}>New · corrected <SortIcon f="corrected" /></th>
                  <th onClick={() => toggleSort('npsDelta')}>Δ corrected <SortIcon f="npsDelta" /></th>
                  <th onClick={() => toggleSort('responses')}>Responses <SortIcon f="responses" /></th>
                  <th onClick={() => toggleSort('replaced')}>Replaced <SortIcon f="replaced" /></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const k = `${e.type}\u0001${e.employee_id}`;
                  const cases = casesByEmp.get(k) || [];
                  const isOpen = expanded === k;
                  return (
                    <React.Fragment key={k}>
                      <tr className={`nl-row ${cases.length ? 'clickable' : ''}`} onClick={() => cases.length && setExpanded(isOpen ? null : k)}>
                        <td className="nl-emp">
                          {cases.length ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="nl-spacer" />}
                          <span className="nl-name">{e.name}</span>
                          <span className="nl-type">{e.type}</span>
                          {e.sensitive && <span className="nl-badge sens" title="A newer answer on one of this person's orders belongs to a different employee"><ShieldAlert size={12} /> sensitive</span>}
                          {e.parityMatch === false && <span className="nl-badge drift" title="Differs from the live system due to answers added since import"><AlertTriangle size={12} /> drift</span>}
                        </td>
                        <td className={`nl-n ${e.salesAppToday ? npsClass(e.salesAppToday.nps) : ''}`}>{e.salesAppToday ? e.salesAppToday.nps : '—'}</td>
                        <td className={`nl-n ${npsClass(e.newSameRules.nps)}`}>{e.newSameRules.nps}</td>
                        <td className={`nl-n ${npsClass(e.newCorrected.nps)}`}>{e.newCorrected.nps}</td>
                        <td className={`nl-n delta ${npsClass(e.npsDelta)}`}>{fmtNps(e.npsDelta)}</td>
                        <td className="nl-muted">{e.newSameRules.total}{e.newCorrected.total !== e.newSameRules.total && <span className="nl-arrow"> → {e.newCorrected.total}</span>}</td>
                        <td className="nl-muted">{e.replaced || ''}</td>
                      </tr>
                      {isOpen && cases.map((c) => (
                        <tr key={k + c.order_key} className="nl-case">
                          <td colSpan={7}>
                            <div className="nl-caserow">
                              <span className="nl-order">{c.order_key} · {c.type}</span>
                              <div className="nl-members">
                                {c.members.map((m, i) => (
                                  <span key={i} className={`nl-mem ${m.status}`}>
                                    {m.local_date} · rating {m.rating} · {m.name}
                                    <b>{m.status === 'counted' ? ' ✓ counts' : ' replaced'}</b>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={7} className="nl-empty">No employees match.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default NpsLedger;
